import { Inject, Injectable } from '@angular/core';
import { Temporal } from '@js-temporal/polyfill';
import { Ticket } from '../ridango.models';
import { AppStartupService } from './app-startup.service';
import Env from '../../env';

export interface AdjustToTransportDayEndOptions {
    timezone?: string;
    ticket?: Ticket | null;
    productType?: string | null;
    fallbackWhenDisabled?: string;
}

export type RidangoDateInput = Date | string | Temporal.Instant;

@Injectable({
    providedIn: 'root'
})
export class DateFormattingService {
    constructor(
        @Inject('ENV') private env: Env,
        private appStartupService: AppStartupService
    ) {}

    // Parse ticket datetimes; zone-less ISO strings use tenant timezone.
    parseToInstant(value: RidangoDateInput, timezone?: string): Temporal.Instant {
        if (value instanceof Temporal.Instant) {
            return value;
        }

        if (value instanceof Date) {
            return Temporal.Instant.fromEpochMilliseconds(value.getTime());
        }

        const timeZone = timezone ?? this.appStartupService.getTimezone() ?? 'Etc/UTC';
        const dateString = value.trim();

        if (this.hasExplicitTimezoneOffset(dateString)) {
            return Temporal.Instant.from(dateString);
        }

        try {
            return Temporal.ZonedDateTime.from(`${dateString}[${timeZone}]`).toInstant();
        } catch {
            return Temporal.PlainDateTime.from(dateString).toZonedDateTime(timeZone).toInstant();
        }
    }

    adjustToTransportDayEnd(instant: Temporal.Instant, options?: AdjustToTransportDayEndOptions): Temporal.Instant {
        const type = options?.productType ?? options?.ticket?.product_type ?? options?.ticket?.product?.product_type;
        const skipTypes = this.getShortPeriodSkipTypes();
        const shouldSkip = type && skipTypes.includes(type);

        if (shouldSkip) {
            return instant;
        }
        if (!this.isTransportDayOffsetEnabled()) {
            return options?.fallbackWhenDisabled ? Temporal.Instant.from(options.fallbackWhenDisabled) : instant;
        }

        const timezone = options?.timezone ?? this.appStartupService.getTimezone();
        const zdt = instant.toZonedDateTimeISO(timezone);
        const transitionMinutes = this.appStartupService.getTransportationDayOffset(instant).total('minutes');
        const transitionHour = Math.floor(transitionMinutes / 60);
        const endOfDayBase = zdt.with({
            hour: 23,
            minute: 59,
            second: 59,
            millisecond: 0
        });
        const adjustedZdt = zdt.hour < transitionHour ? endOfDayBase.subtract({ days: 1 }) : endOfDayBase;
        return adjustedZdt.toInstant();
    }

    getAdjustedValidityRange(
        start: Temporal.Instant,
        end: Temporal.Instant,
        options?: AdjustToTransportDayEndOptions
    ): [Temporal.Instant, Temporal.Instant] {
        return [start, this.adjustToTransportDayEnd(end, options)];
    }

    private getShortPeriodSkipTypes(): string[] {
        return (
            this.env.FORMATTING?.SHORT_PERIOD_PRODUCT_TYPES_SKIP_OFFSET ??
            (this.env.PURCHASE?.SKIP_OFFSET_FOR_DURATION_BASED_TICKETS ? ['M', 'H'] : [])
        );
    }

    private isTransportDayOffsetEnabled(): boolean {
        return this.appStartupService.isTransportationDayOffsetEnabled();
    }

    private hasExplicitTimezoneOffset(dateString: string): boolean {
        return dateString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateString);
    }
}
